import { useAuthReady } from './useAuthReady';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  encodeTransferBatch,
  sendTronTransfer,
  sendTronApprove,
  signTronDisperse,
  waitForTronAllowance,
  tronAddressFromMnemonic,
} from '@incpt/blockchain';
import {
  NATIVE_SEND_MODES,
  SELF_PAID_TOKEN_MODES,
  TRANSFER_FEE_USD,
  parseXrpDestinationTag,
} from '@incpt/shared';
import {
  createTransfer,
  prepareTronTransfer,
  broadcastTronTransfer,
  patchTransfer,
  fetchTransfers,
  type CreateTransferParams,
  type Chain,
  type Token,
} from '../lib/transfers';
import { apiUrl } from '../lib/api';
import { getSeed } from '../lib/wallet';
import type { QueryClient } from '@tanstack/react-query';
import { dbg } from '../lib/debug';
import { authAuthorizationValue } from './useTelegram';

/** Pull a readable message out of viem / Account Kit / plain Error throws.
 *  Prefer `details` — viem's shortMessage is often a generic wrapper
 *  ("Invalid parameters were provided…") while Alchemy's real cause sits in
 *  details/cause. This string is stored as failureReason for the ops log; the
 *  Send toast maps codes / falls back to a user-facing generic string. */
function errText(err: unknown, depth = 0): string {
  if (err == null) return 'unknown';
  if (typeof err === 'string') return err;
  if (depth > 4) return String(err);

  const push = (out: string[], value: string | undefined | null) => {
    const s = (value ?? '').trim();
    if (!s) return;
    if (out.some((u) => u.includes(s) || s.includes(u))) return;
    out.push(s);
  };

  if (err instanceof Error) {
    const any = err as Error & { shortMessage?: string; details?: string; cause?: unknown };
    const out: string[] = [];
    push(out, any.details);
    if (any.cause != null) push(out, errText(any.cause, depth + 1));
    push(out, any.shortMessage || any.message);
    return out.join(' — ') || String(err);
  }
  if (typeof err === 'object') {
    const o = err as { shortMessage?: string; details?: string; message?: string; cause?: unknown };
    const out: string[] = [];
    push(out, o.details);
    if (o.cause != null) push(out, errText(o.cause, depth + 1));
    push(out, o.shortMessage || o.message);
    if (out.length) return out.join(' — ');
  }
  return String(err);
}

/** Transfer history + a `send` mutation that refreshes balances + history. */

/**
 * When this wallet's Monero address was registered, read from the wallet query
 * already in the cache.
 *
 * Monero is the only chain that needs this. Every other scan is an index
 * lookup; Monero's is a walk forward from a starting block, and the starting
 * block is the day the address came into existence. Null when the wallet has
 * not loaded, which the scanner treats as "look further back than you need
 * to" rather than "start from now".
 */
function moneroAddressCreatedAt(qc: QueryClient, index: number): string | null {
  const wallet = qc
    .getQueriesData<{
      wallet: {
        addresses: { chain: string; derivationIndex?: number; createdAt?: string }[];
      } | null;
    }>({ queryKey: ['wallet'] })
    .map(([, d]) => d?.wallet)
    .find(Boolean);
  const row = wallet?.addresses.find(
    (a) => a.chain === 'xmr' && (a.derivationIndex ?? 0) === index,
  );
  return row?.createdAt ?? null;
}

export function useTransfers(filter?: { chain?: Chain; token?: Token }) {
  const { canFetch: ready, initData } = useAuthReady();
  const qc = useQueryClient();

  const historyQ = useQuery({
    queryKey: filter ? ['transfers', filter.chain ?? '', filter.token ?? ''] : ['transfers'],
    enabled: ready,
    queryFn: () => fetchTransfers(initData, filter),
  });

  const send = useMutation({
    mutationFn: async ({
      reauthToken,
      fromIndex = 0,
      idempotencyKey,
      // Card issue must wait until the server has recorded a settled payment —
      // verifyCardPayment needs a broadcast txHash (and proves it on-chain).
      // Regular sends and card top-ups stay optimistic so the UI can close right
      // after broadcast; top-up fires /recharge in the background.
      awaitSettlement = false,
      ...params
    }: CreateTransferParams & {
      reauthToken?: string;
      fromIndex?: number;
      idempotencyKey?: string;
      awaitSettlement?: boolean;
    }) => {
      // Decrypting the seed needs a server round-trip (seed-key fetch) + local
      // crypto — all independent of creating the transfer + renting energy. Kick
      // it off NOW so it overlaps the (slow) server work instead of running after
      // it. getSeed never throws (returns null on failure), and the reauth token
      // is a stateless JWT, so using it here and on createTransfer in parallel is
      // safe. On the stub/replay paths the fetched seed is simply unused.
      const seedPromise = getSeed(initData, reauthToken).catch(() => null);

      let res = await createTransfer(initData, params, reauthToken, idempotencyKey);

      // Stub / already-finalized path → the server settled it. Require an actual
      // success status: a failed (or still-pending) row without `mode` used to
      // resolve the mutation and trigger the UI success state with no broadcast.
      // Treasury is required ONLY for the sponsored modes — a self-paid send
      // carries no treasury (network fee only) and must not be shunted into
      // the stub.
      //
      // A mode missing from these lists lands in the stub branch and is never
      // broadcast, which fails silently: the mutation resolves and the UI
      // shows success. So the self-paid list comes from shared rather than
      // being retyped here, and a new mode has one place to be added.
      const treasuryModes = ['client-7702', 'client-tron', 'client-tron-disperse'];
      const selfPaidModes: readonly string[] = [...NATIVE_SEND_MODES, ...SELF_PAID_TOKEN_MODES];
      const isTreasuryMode = !!res.mode && treasuryModes.includes(res.mode);
      const isSelfPaidMode = !!res.mode && selfPaidModes.includes(res.mode);
      if ((!isTreasuryMode && !isSelfPaidMode) || (isTreasuryMode && !res.treasury)) {
        if (res.transfer.status === 'failed') {
          throw new Error(res.transfer.failureReason ?? 'transfer_failed');
        }
        if (res.transfer.status === 'pending' || res.transfer.status === 'submitted') {
          // Hash already claimed; server is still reconciling the receipt.
          throw new Error('transfer_verification_pending');
        }
        if (res.transfer.status !== 'success') {
          throw new Error('transfer_incomplete');
        }
        return res.transfer;
      }

      // Server always returns the authoritative fee; the constant is a safety net.
      let feeUsd = res.feeUsd ?? TRANSFER_FEE_USD;

      // Read only by the stablecoin (treasury) branches below: the guard above
      // ensures res.treasury exists for those modes, and native modes (whose
      // token may be ETH/BTC) return before ever touching these.
      const stableToken = params.token as 'USDT' | 'USDC';
      let treasury = res.treasury as string;
      let durableTronBroadcastStarted = false;

      // Helpers for background finalization — must never throw to the caller.
      const bgInvalidate = () => {
        qc.invalidateQueries({ queryKey: ['transfers'] });
        qc.invalidateQueries({ queryKey: ['balances'] });
      };

      /** Await PATCH and surface an on-chain revert as a thrown error. */
      const settle = async (txHash: string, feeTxHash?: string) => {
        for (let attempt = 0; attempt < 6; attempt++) {
          const updated = await patchTransfer(initData, res.transfer.id, {
            txHash,
            ...(feeTxHash ? { feeTxHash } : {}),
          });
          bgInvalidate();
          if (updated.status === 'failed') {
            throw new Error(updated.failureReason ?? 'transfer_failed');
          }
          if (updated.status === 'success') return updated;
          if (attempt < 5) await new Promise((resolve) => setTimeout(resolve, 1_500));
        }
        throw new Error('transfer_verification_pending');
      };

      try {
        // Seed was fetched in parallel above. A missing/undecryptable seed marks
        // the pending transfer failed instead of leaving it stuck as "pending"
        // (which the UI would show as sent).
        const mnemonic = await seedPromise;
        if (!mnemonic) throw new Error('seed_unavailable');

        // Silent BTC-address backfill: on every send the seed is in hand (PIN
        // users included via the reauth token) — the cheapest moment to complete
        // wallets registered before Bitcoin support. Fire-and-forget.
        void (async () => {
          const { ensureWalletAddresses } = await import('../lib/addressBackfill');
          const wallet =
            qc
              .getQueriesData<{
                wallet: { addresses: { chain: string; derivationIndex?: number }[] } | null;
              }>({ queryKey: ['wallet'] })
              .map(([, d]) => d?.wallet)
              .find(Boolean) ?? null;
          await ensureWalletAddresses(initData, mnemonic, wallet, () =>
            qc.invalidateQueries({ queryKey: ['wallet'] }),
          );
        })().catch(() => {});

        if (
          res.mode === 'client-native-eth' ||
          res.mode === 'client-native-bnb' ||
          res.mode === 'client-native-hype'
        ) {
          // ── EVM coin: plain EOA value tx, user pays own gas ─────────────────
          // One branch per account model, not per chain: the signer is the same
          // value transfer, and what differs is the proxy it broadcasts through
          // and the chainId the signature commits to.
          const chain = params.chain;
          dbg(`send native ${chain}: amt=${params.amount}`);
          const [{ sendNativeEth }, { parseUnits }] = await Promise.all([
            import('../lib/nativeEth'),
            import('viem'),
          ]);
          const txHash = await sendNativeEth({
            mnemonic,
            initData,
            rpcProxyUrl: apiUrl(`/v1/rpc/${chain}`),
            to: params.to as `0x${string}`,
            amountWei: parseUnits(params.amount, 18),
            index: fromIndex,
            chain,
          });
          dbg(`send native ${chain}: mined ${txHash.slice(0, 12)}…`);
          return settle(txHash);
        }

        if (res.mode === 'client-erc20') {
          // ── Self-paid token: ordinary ERC-20 transfer, user pays own gas ───
          const chain = params.chain;
          dbg(`send erc20 ${chain}: ${params.amount} ${params.token}`);
          const { sendSelfPaidToken } = await import('../lib/erc20');
          const txHash = await sendSelfPaidToken({
            mnemonic,
            initData,
            rpcProxyUrl: apiUrl(`/v1/rpc/${chain}`),
            chain,
            token: params.token as 'USDT' | 'USDC',
            to: params.to as `0x${string}`,
            amount: params.amount,
            index: fromIndex,
          });
          dbg(`send erc20 ${chain}: mined ${txHash.slice(0, 12)}…`);
          return settle(txHash);
        }

        if (res.mode === 'client-zec') {
          // ── Transparent ZEC: UTXOs + chain tip + the LIVE consensus branch
          // id from the proxy, signed here, broadcast through the proxy ───────
          dbg(`send zec: amt=${params.amount}`);
          const { sendZcash } = await import('../lib/zcashSend');
          const txid = await sendZcash({
            mnemonic,
            initData,
            to: params.to,
            amount: params.amount,
            index: fromIndex,
          });
          dbg(`send zec: broadcast ${txid.slice(0, 12)}…`);
          return settle(txid);
        }

        if (res.mode === 'client-xmr') {
          // ── Monero: scan first, then build and relay on the device ────────
          //
          // Unlike every other chain, this send cannot start from an amount
          // and an address alone. A Monero transaction spends specific outputs
          // and the wallet only knows which it owns after scanning, so the
          // scan is part of sending — and on a wallet left closed a while, it
          // is the slow part. The real fee comes back from the built
          // transaction; the quote shown before this was a typical figure.
          dbg(`send xmr: amt=${params.amount}`);
          const { openMoneroWallet } = await import('../lib/monero');
          const { parseUnits } = await import('viem');
          const wallet = await openMoneroWallet({
            mnemonic,
            initData,
            index: fromIndex,
            // Where the scan starts. An address cannot have received anything
            // before it existed, so its registration time is the earliest
            // block worth looking at — and getting this wrong in the other
            // direction (starting from "now") would silently skip deposits.
            createdAt: moneroAddressCreatedAt(qc, fromIndex),
          });
          try {
            await wallet.sync((p) => dbg(`send xmr: scanning ${p.percent}%`));
            const { hash, feePiconero } = await wallet.send(
              params.to,
              parseUnits(params.amount, 12),
            );
            dbg(`send xmr: relayed ${hash.slice(0, 12)}… fee=${feePiconero}`);
            return settle(hash);
          } finally {
            // Always, so the scan just paid for is kept even when the send
            // failed — otherwise a retry rescans from the beginning.
            await wallet.close();
          }
        }

        if (res.mode === 'client-native-ton') {
          // ── Toncoin: the wallet contract deploys itself on this first send
          // if it does not exist yet ─────────────────────────────────────────
          dbg(`send ton: amt=${params.amount}`);
          const { sendTon } = await import('../lib/tonSend');
          const hash = await sendTon({
            mnemonic,
            initData,
            to: params.to,
            amount: params.amount,
            index: fromIndex,
          });
          dbg(`send ton: broadcast ${hash.slice(0, 12)}…`);
          return settle(hash);
        }

        if (res.mode === 'client-jetton') {
          // ── Jetton (USDT): relayed through our own jetton wallet, gas in
          // TON, the unused reserve returned to us ───────────────────────────
          dbg(`send jetton: ${params.amount} ${params.token}`);
          const { sendTonJetton } = await import('../lib/tonSend');
          const hash = await sendTonJetton({
            mnemonic,
            initData,
            token: params.token as 'USDT' | 'USDC',
            to: params.to,
            amount: params.amount,
            index: fromIndex,
          });
          dbg(`send jetton: broadcast ${hash.slice(0, 12)}…`);
          return settle(hash);
        }

        if (res.mode === 'client-native-sol') {
          // ── SOL: the fee leaves the same balance as the amount ─────────────
          dbg(`send sol: amt=${params.amount}`);
          const { sendSolana } = await import('../lib/solana');
          const signature = await sendSolana({
            mnemonic,
            initData,
            rpcProxyUrl: apiUrl('/v1/rpc/sol'),
            to: params.to,
            amount: params.amount,
            index: fromIndex,
          });
          dbg(`send sol: confirmed ${signature.slice(0, 12)}…`);
          return settle(signature);
        }

        if (res.mode === 'client-spl') {
          // ── SPL: amount from the token account, fee + any rent from SOL ────
          dbg(`send spl: ${params.amount} ${params.token}`);
          const { sendSolanaToken } = await import('../lib/solana');
          const signature = await sendSolanaToken({
            mnemonic,
            initData,
            rpcProxyUrl: apiUrl('/v1/rpc/sol'),
            token: params.token as 'USDT' | 'USDC',
            to: params.to,
            amount: params.amount,
            index: fromIndex,
          });
          dbg(`send spl: confirmed ${signature.slice(0, 12)}…`);
          return settle(signature);
        }

        if (res.mode === 'client-native-trx') {
          // ── Native TRX: TransferContract, user pays own bandwidth ───────────
          dbg(`send native trx: amt=${params.amount}`);
          const [{ sendTronNative }, { parseUnits }] = await Promise.all([
            import('@incpt/blockchain'),
            import('viem'),
          ]);
          const txHash = await sendTronNative({
            mnemonic,
            index: fromIndex,
            fullHost: apiUrl('/v1/rpc/trx'),
            headers: { Authorization: authAuthorizationValue(initData) },
            to: params.to,
            amountSun: parseUnits(params.amount, 6),
          });
          dbg(`send native trx: broadcast ${txHash.slice(0, 12)}…`);
          return settle(txHash);
        }

        if (res.mode === 'client-btc') {
          // ── BTC: fetch UTXOs right before signing (freshest set), build+sign
          // locally, broadcast via the proxy. NEVER wait for a confirmation here
          // (10+ min) — PATCH records the txid and the server/deposit sync
          // reconciles; the fee shown equals the fee paid (same selectUTXO).
          dbg(`send btc: amt=${params.amount}`);
          const [btcSigner, btcApi, { parseUnits }] = await Promise.all([
            import('@incpt/blockchain/btc'),
            import('../lib/btc'),
            import('viem'),
          ]);
          const from = btcSigner.btcAddressFromMnemonic(mnemonic, fromIndex);
          const [utxos, feeRate] = await Promise.all([
            btcApi.fetchBtcUtxos(initData, from),
            btcApi.fetchBtcFeeRate(initData),
          ]);
          const signed = btcSigner.buildAndSignBtcTx({
            // Confirmed outputs only — never chain on unconfirmed txs.
            utxos: utxos
              .filter((u) => u.status.confirmed)
              .map((u) => ({ txid: u.txid, vout: u.vout, valueSat: BigInt(u.value) })),
            to: params.to,
            amountSat: parseUnits(params.amount, 8),
            feeRateSatVb: feeRate,
            changeAddress: from,
            mnemonic,
            index: fromIndex,
          });
          dbg(`send btc: fee=${signed.feeSat}sat vsize=${signed.vsize}`);
          const txid = await btcApi.broadcastBtcTx(initData, signed.hex);
          // BTC confirmation takes minutes. Record and claim the txid now; the
          // server reconciler verifies inputs/recipient/amount after confirmation.
          await patchTransfer(initData, res.transfer.id, { txHash: txid });
          bgInvalidate();
          return { ...res.transfer, status: 'submitted', txHash: txid };
        }

        if (res.mode === 'client-xrp') {
          // ── XRP: the ledger supplies everything the signature commits to —
          // the sender's next sequence, the current fee and a ledger index to
          // expire against — so they are fetched together right before signing.
          //
          // Two refusals happen HERE rather than at the ledger, because by the
          // time the ledger says no the user has already committed:
          //   - a destination that requires a tag, which we cannot attach;
          //   - a first payment to an unfunded account below the base reserve,
          //     which the ledger rejects outright (tecNO_DST_INSUFF_XRP).
          dbg(`send xrp: amt=${params.amount}`);
          const [xrpSigner, xrpApi, { parseUnits }] = await Promise.all([
            import('@incpt/blockchain/xrp'),
            import('../lib/xrp'),
            import('viem'),
          ]);
          const from = xrpSigner.xrpAddressFromMnemonic(mnemonic, fromIndex);
          const [me, dest] = await Promise.all([
            xrpApi.fetchXrpAccount(initData, from),
            xrpApi.fetchXrpAccount(initData, params.to),
          ]);
          // The tag comes back from the server's own record of the transfer,
          // not from the form: the API validated and stored it, and signing
          // what it stored is what keeps the two from disagreeing.
          const memo = res.transfer.memo ?? params.memo;
          const destinationTag =
            memo != null && memo !== '' ? parseXrpDestinationTag(memo) : undefined;
          if (dest.requiresDestinationTag && destinationTag === undefined) {
            throw new Error('xrp_destination_tag_required');
          }
          const amountDrops = parseUnits(params.amount, 6);
          if (!dest.exists && amountDrops < BigInt(me.reserveBaseDrops)) {
            throw new Error('xrp_below_destination_reserve');
          }
          const signed = xrpSigner.buildAndSignXrpPayment({
            mnemonic,
            index: fromIndex,
            to: params.to,
            amountDrops,
            feeDrops: BigInt(me.baseFeeDrops),
            sequence: me.sequence,
            lastLedgerSequence: me.ledgerIndex + xrpApi.XRP_LEDGER_OFFSET,
            ...(destinationTag !== undefined ? { destinationTag } : {}),
          });
          dbg(`send xrp: fee=${me.baseFeeDrops}drops seq=${me.sequence}`);
          const hash = await xrpApi.submitXrpTx(initData, signed.blob);
          await patchTransfer(initData, res.transfer.id, { txHash: hash });
          bgInvalidate();
          return { ...res.transfer, status: 'submitted', txHash: hash };
        }

        if (res.mode === 'client-doge') {
          // ── DOGE: same shape as the BTC branch above — fetch outputs right
          // before signing, build+sign locally, broadcast through the proxy,
          // never wait for a confirmation (a block is a minute, but the
          // reconciler owns the outcome either way).
          //
          // The extra step over Bitcoin is the per-input funding transaction:
          // a legacy input is signed against the whole previous transaction,
          // not against a script and a value. They are fetched in parallel and
          // an input whose parent cannot be read is dropped rather than
          // guessed at — spending it would produce an unsignable transaction.
          dbg(`send doge: amt=${params.amount}`);
          const [dogeSigner, dogeApi, { parseUnits }] = await Promise.all([
            import('@incpt/blockchain/doge'),
            import('../lib/doge'),
            import('viem'),
          ]);
          const from = dogeSigner.dogeAddressFromMnemonic(mnemonic, fromIndex);
          const rows = await dogeApi.fetchDogeUtxos(initData, from);
          const utxos = (
            await Promise.all(
              rows.map(async (u) => {
                try {
                  const prevTxHex = await dogeApi.fetchDogeRawTx(initData, u.txid);
                  return {
                    txid: u.txid,
                    vout: u.vout,
                    valueKoinu: BigInt(u.valueKoinu),
                    prevTxHex,
                  };
                } catch {
                  return null;
                }
              }),
            )
          ).filter((u): u is NonNullable<typeof u> => u !== null);
          if (utxos.length === 0) throw new Error('doge_utxos_unavailable');
          const signed = dogeSigner.buildAndSignDogeTx({
            utxos,
            to: params.to,
            amountKoinu: parseUnits(params.amount, 8),
            changeAddress: from,
            mnemonic,
            index: fromIndex,
          });
          dbg(`send doge: fee=${signed.feeKoinu}koinu bytes=${signed.bytes}`);
          const txid = await dogeApi.broadcastDogeTx(initData, signed.hex);
          await patchTransfer(initData, res.transfer.id, { txHash: txid });
          bgInvalidate();
          return { ...res.transfer, status: 'submitted', txHash: txid };
        }

        if (res.mode === 'client-7702') {
          // ── ETH path ─────────────────────────────────────────────────────────
          // Bundler returns only an opHash immediately. We wait briefly on the UI
          // (~20s) for the common case; if inclusion is slow we report the opHash,
          // close optimistically, and finish settle in the background. Card flows
          // (awaitSettlement) stay on a longer wait — verify needs a real txHash.
          const principal = res.preparedAmount ?? params.amount;
          dbg(`send eth: uo fee=$${feeUsd} amt=${principal} ${params.token}`);
          const calls = encodeTransferBatch({
            chain: 'eth',
            token: stableToken,
            to: params.to,
            amount: principal,
            treasury,
            feeUsd,
          });
          const { submitGaslessEvm, isUserOpInclusionTimeout } = await import('../lib/gasless');
          const { opHash, waitForTx } = await submitGaslessEvm({
            mnemonic,
            initData,
            rpcProxyUrl: apiUrl('/v1/rpc/eth'),
            calls,
            index: fromIndex,
          });
          // Persist the bundler hash immediately. If the WebView dies while
          // waiting for inclusion, the server can still reconcile the UserOp
          // and must not treat this as an unsigned/no-broadcast timeout.
          await patchTransfer(initData, res.transfer.id, { userOpHash: opHash });
          try {
            const txHash = await waitForTx(awaitSettlement ? 'bg' : 'ui');
            dbg(`send eth: mined ${txHash.slice(0, 12)}…`);
            return settle(txHash, txHash);
          } catch (waitErr) {
            const waitMsg = errText(waitErr);
            if (!isUserOpInclusionTimeout(waitMsg)) throw waitErr;

            dbg(`send eth: uo wait timeout ${opHash.slice(0, 12)}…`);
            if (awaitSettlement) {
              // Card payment must not look "paid" without a mined hash.
              throw new Error(`uo_inclusion_timeout:${opHash}`);
            }

            // Regular send: don't park the user. Keep polling in the background;
            // server also reconciles via eth_getUserOperationReceipt.
            void waitForTx('bg')
              .then((txHash) => settle(txHash, txHash))
              .catch((e) =>
                console.error('[send] eth bg settle failed:', errText(e).slice(0, 180)),
              );
            bgInvalidate();
            return { ...res.transfer, status: 'submitted' } as typeof res.transfer;
          }
        }

        if (res.mode === 'client-tron-disperse' && res.disperseContract) {
          // ── Tron disperse optimistic path ────────────────────────────────────
          // approve + waitForAllowance MUST complete before broadcast (these are
          // pre-conditions for disperse, not the send itself) — keep them in the
          // critical path so a failure here still shows an error toast.
          const fullHost = apiUrl('/v1/rpc/trx');
          const headers = { Authorization: authAuthorizationValue(initData) };
          const owner = tronAddressFromMnemonic(mnemonic, fromIndex);
          if (res.preparedOwner && owner !== res.preparedOwner) {
            throw new Error('prepared_payload_mismatch');
          }
          let tronToken = res.preparedToken ?? stableToken;
          let recipient = res.preparedRecipient ?? params.to;
          let principal = res.preparedAmount ?? params.amount;
          if (res.needsApproval) {
            await sendTronApprove({
              mnemonic,
              index: fromIndex,
              fullHost,
              headers,
              token: tronToken,
              spender: res.disperseContract,
            });
            // The approve must be mined before the disperse can transferFrom;
            // otherwise the allowance is still 0 and the disperse reverts. Wait
            // until the allowance is readable on-chain (covers the amounts).
            await waitForTronAllowance({
              rpcUrl: fullHost,
              token: tronToken,
              owner,
              spender: res.disperseContract,
              // approve() grants uint256.max. This amount only confirms that the
              // allowance is visible on-chain before exact disperse simulation.
              amounts: [principal, String(feeUsd)],
              headers,
            });
          }
          if (res.needsPreparation) {
            // The call could not be simulated before allowance existed. The
            // server now simulates the exact disperse calldata, rents only the
            // measured deficit, and returns the final fee. This is separate from
            // needsApproval so a retry after a confirmed approve does not approve
            // (and spend sponsored energy) a second time.
            res = await prepareTronTransfer(initData, res.transfer.id, reauthToken);
            if (!res.disperseContract || !res.treasury) {
              throw new Error('transfer_incomplete');
            }
            feeUsd = res.feeUsd ?? TRANSFER_FEE_USD;
            treasury = res.treasury;
            tronToken = res.preparedToken ?? tronToken;
            recipient = res.preparedRecipient ?? recipient;
            principal = res.preparedAmount ?? principal;
            if (res.preparedOwner && owner !== res.preparedOwner) {
              throw new Error('prepared_payload_mismatch');
            }
          }

          // Sign locally, then let the API durably store the exact signed bytes
          // before it performs the first broadcast.
          //
          // A $0 fee (a card payment the project absorbs) is sent as a
          // SINGLE-recipient disperse. Keeping a zero-value treasury leg would
          // buy a second storage-slot write worth of energy for a transfer that
          // moves nothing, and the server priced (and rented for) one leg.
          const recipients = feeUsd > 0 ? [recipient, treasury] : [recipient];
          const amounts = feeUsd > 0 ? [principal, String(feeUsd)] : [principal];
          const { txHash, signedTransaction } = await signTronDisperse({
            mnemonic,
            index: fromIndex,
            fullHost,
            headers,
            token: tronToken,
            contract: res.disperseContract,
            recipients,
            amounts,
          });
          durableTronBroadcastStarted = true;
          const recorded = await broadcastTronTransfer(
            initData,
            res.transfer.id,
            signedTransaction,
            reauthToken,
          );
          bgInvalidate();
          if (recorded.status === 'failed') {
            throw new Error(recorded.failureReason ?? 'transfer_failed');
          }

          // Card issue: wait for the server to confirm settlement (and catch
          // Out-of-Energy reverts) before calling apply with this id. Top-up
          // returns after broadcast and settles inside /recharge instead.
          if (awaitSettlement) return settle(txHash, txHash);

          return { ...res.transfer, status: 'submitted', txHash } as typeof res.transfer;
        }

        // ── Tron direct path (one transfer when feeUsd=0, else legacy two) ────
        const fullHost = apiUrl('/v1/rpc/trx');
        const headers = { Authorization: authAuthorizationValue(initData) };
        const principal = res.preparedAmount ?? params.amount;
        const txHash = await sendTronTransfer({
          mnemonic,
          index: fromIndex,
          fullHost,
          headers,
          token: stableToken,
          to: params.to,
          amount: principal,
        });
        // Treasury fee is part of settlement proof; do not report success until
        // both separately broadcast legacy transactions are known. An absorbed
        // ($0) fee has no leg at all — settlement verification skips it too.
        const feeTxHash =
          feeUsd > 0
            ? await sendTronTransfer({
                mnemonic,
                index: fromIndex,
                fullHost,
                headers,
                token: stableToken,
                to: treasury,
                amount: String(feeUsd),
              })
            : txHash;

        if (awaitSettlement) return settle(txHash, feeTxHash);

        const recorded = await patchTransfer(
          initData,
          res.transfer.id,
          { txHash, feeTxHash },
          { keepalive: true },
        );
        bgInvalidate();
        if (recorded.status === 'failed') {
          throw new Error(recorded.failureReason ?? 'transfer_failed');
        }

        return { ...res.transfer, status: 'submitted', txHash } as typeof res.transfer;
      } catch (err) {
        // Broadcast / signing / approve failed — mark the pending row so it
        // doesn't linger and so history can offer a Resend if applicable.
        const msg = errText(err);
        dbg(`send fail ${res.mode}: ${msg.slice(0, 180)}`);
        console.error(`[send] ${res.mode} transfer failed:`, err);
        const energyFail =
          /account resource insufficient|out_of_energy|onchain_reverted|(?:bandwith|bandwidth)_error|insufficient.*(energy|bandwidth)/i.test(
            msg,
          );
        const recoverablePreparation =
          /transfer_preparing|transfer_verification_pending|uo_inclusion_timeout|energy_estimation_failed|rpc_unavailable|sponsored_resources_expired|rental_result_uncertain|preparation_lease_expired/i.test(
            msg,
          ) || durableTronBroadcastStarted;
        if (!recoverablePreparation) {
          await patchTransfer(initData, res.transfer.id, {
            status: 'failed',
            // Full RPC/AA text for ops-log via PATCH; toast stays user-facing.
            failureReason: energyFail ? 'insufficient_energy' : msg.slice(0, 500),
          }).catch((e) => console.error('[send] could not mark transfer failed:', e));
        }
        // Re-throw a plain Error so parseTransferError can map known codes
        // (the toast never shows raw RPC text).
        throw new Error(msg);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      qc.invalidateQueries({ queryKey: ['balances'] });
    },
  });

  return {
    transfers: historyQ.data ?? [],
    loading: historyQ.isLoading,
    send,
  };
}
